import { useCallback, useMemo, useRef, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import { IconAddWindow, IconPlus } from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import NavList from '../layout/NavList/NavList'
import { NavFilterLead, useFilterOpen } from '../layout/NavList/NavFilterLead'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import { MultiSelect } from '../components/inputs'
import { useAuth } from '../context/AuthContext'
import { useModal } from '../context/ModalContext'
import { useTopics } from '../hooks/useTopics'
import { useChatrooms } from '../hooks/useChatrooms'
import { useChatEvents } from '../hooks/useChatEvents'
import { usePageTitle } from '../components/DocumentTitle'
import { topicLabelFor } from '../utils/topicLabel'
import ChatList from '../components/chatroom/ChatList'
import ChatWindowBody from '../components/chatroom/ChatWindowBody'
import {
  leaveChatroom,
  muteChatroom,
  unmuteChatroom,
  updateChatroom,
} from '../services/chatrooms'
import '../components/chatroom/Chatroom.css'

const LIMITS = { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 }

function byRecent(a, b) {
  return new Date(b.updatedAt) - new Date(a.updatedAt)
}

function upsertRoom(prev, room) {
  if (!room?._id) return prev
  const rest = prev.filter((entry) => String(entry._id) !== String(room._id))
  return [room, ...rest].sort(byRecent)
}

function ChatPage() {
  const { user } = useAuth()
  const { openModal } = useModal()
  const { topics: topicOptions } = useTopics()
  const [q, setQ] = useState('')
  const [topicFilter, setTopicFilter] = useState([])
  const [filterOpen, toggleFilter] = useFilterOpen('ct-chat-filter-open')
  const [notice, setNotice] = useState('')
  const [revision, setRevision] = useState(0)
  const topicKey = topicFilter.map((topic) => topic._id).join(',')
  const { rooms, setRooms, status, reload } = useChatrooms(q, topicKey)

  const grid = useWindowGrid({
    storageKey: 'ct-grid-chat-v1',
    limits: LIMITS,
    initialType: 'chat',
  })

  const columnsRef = useRef(grid.columns)
  columnsRef.current = grid.columns
  const roomsRef = useRef(rooms)
  roomsRef.current = rooms

  const activeId = useMemo(() => {
    for (const col of grid.columns) {
      const win = col.windows.find((entry) => entry.id === grid.activeId)
      if (win) return win.entityId
    }
    return null
  }, [grid.columns, grid.activeId])

  const remember = useCallback((room) => {
    setRooms((prev) => upsertRoom(prev, room))
  }, [setRooms])

  const clearWindows = useCallback(
    (chatroomId) => {
      columnsRef.current.forEach((col) => {
        col.windows.forEach((win) => {
          if (String(win.entityId) === String(chatroomId)) grid.setWindowEntity(win.id, null)
        })
      })
    },
    [grid]
  )

  const dropRoom = useCallback(
    (chatroomId) => {
      setRooms((prev) => prev.filter((room) => String(room._id) !== String(chatroomId)))
      clearWindows(chatroomId)
    },
    [setRooms, clearWindows]
  )

  useChatEvents((name, payload) => {
    const id = payload.chatroomId || payload.room?._id
    if (name === 'room:delete') {
      dropRoom(id)
      return
    }
    if (name === 'room:membership' && payload.action === 'removed' && String(payload.userId) === String(user?._id)) {
      dropRoom(id)
      return
    }
    if (name === 'room:update' && payload.room) {
      remember(payload.room)
      return
    }
    if ((payload.preview || name === 'message:delete' || name === 'reply:delete') && id) {
      if (!roomsRef.current.some((room) => String(room._id) === String(id))) {
        reload()
        return
      }
      setRooms((prev) =>
        prev
          .map((room) =>
            String(room._id) === String(id)
              ? { ...room, latestMessage: payload.preview || null, updatedAt: new Date().toISOString(), active: true }
              : room
          )
          .sort(byRecent)
      )
    }
    if (payload.removedRoomIds) {
      payload.removedRoomIds.forEach((removed) => dropRoom(removed))
    }
  })

  const openChat = useCallback(
    (room) => {
      if (!room?._id) return
      grid.selectEntity({ type: 'chat', entityId: room._id })
    },
    [grid]
  )

  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(`You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`)
      return
    }
    grid.addWindow({ type: 'chat' })
  }, [grid])

  const handleNew = () =>
    openModal('create-chat', {
      onCreated: (room) => {
        remember(room)
        openChat(room)
      },
    })

  const saved = useCallback((room) => {
    remember(room)
    setRevision((n) => n + 1)
  }, [remember])

  const renderMenuItems = useCallback(
    (win) => {
      const room = roomsRef.current.find((entry) => String(entry._id) === String(win.entityId))
      if (!room) return null
      const id = room._id
      const lastMember = (room.members || []).length <= 1
      return (
        <>
          <button type="button" role="menuitem" onClick={() => openModal('chat-details', { room })}>
            Details
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() =>
              openModal('chat-text', {
                title: 'Name',
                label: 'Name',
                value: room.label || '',
                onSave: async (label) => saved(await updateChatroom(id, { label })),
              })
            }
          >
            Name
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() =>
              openModal('chat-text', {
                title: 'Description',
                label: 'Description',
                value: room.description || '',
                multiline: true,
                onSave: async (description) => saved(await updateChatroom(id, { description })),
              })
            }
          >
            Description
          </button>
          {room.isManager ? (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => openModal('chat-people', { mode: 'members', room, onSaved: saved })}
              >
                Members
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => openModal('chat-people', { mode: 'managers', room, onSaved: saved })}
              >
                Managers
              </button>
            </>
          ) : null}
          <button type="button" role="menuitem" onClick={() => openModal('chat-topics', { room, onSaved: saved })}>
            Topics
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() =>
              openModal('chat-attach', {
                items: room.items || [],
                guides: room.guides || [],
                onSave: async ({ items, guides }) =>
                  saved(
                    await updateChatroom(id, {
                      items: items.map((item) => item._id),
                      guides: guides.map((guide) => guide._id),
                    })
                  ),
              })
            }
          >
            Items and guides
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              const next = room.muted ? await unmuteChatroom(id) : await muteChatroom(id)
              remember(next)
            }}
          >
            {room.muted ? 'Unmute' : 'Mute'}
          </button>
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            onClick={() =>
              openModal('confirm', {
                title: lastMember ? 'Delete chat' : 'Leave chat',
                message: lastMember
                  ? 'You are the last person here. The chat and its messages will be deleted.'
                  : 'You will no longer see this chat.',
                confirmLabel: lastMember ? 'Delete' : 'Leave',
                danger: true,
                onConfirm: () => leaveChatroom(id),
                onConfirmed: () => dropRoom(id),
              })
            }
          >
            {lastMember ? 'Delete' : 'Leave'}
          </button>
        </>
      )
    },
    [openModal, remember, dropRoom, saved]
  )

  const getTitle = useCallback(
    (win) => {
      if (!win.entityId) return 'New window'
      const room = rooms.find((entry) => String(entry._id) === String(win.entityId))
      return room?.displayLabel || 'Loading…'
    },
    [rooms]
  )
  usePageTitle(activeId ? getTitle({ entityId: activeId }) : '')

  const renderWindow = useCallback(
    (win) => (
      <ChatWindowBody
        chatId={win.entityId}
        revision={revision}
        onRoom={remember}
        onMissing={dropRoom}
      />
    ),
    [revision, remember, dropRoom]
  )

  const filtered = topicFilter.length > 0

  return (
    <MainLayout
      storageId="chat-layout"
      navLocation={<NavLocation title="Chat" />}
      navActions={[
        { key: 'add-window', label: 'Add window', icon: <IconAddWindow />, onClick: handleAddWindow },
        { key: 'new', label: 'New chat', icon: <IconPlus />, onClick: handleNew },
      ]}
      navDock={
        <GridDock
          items={grid.minimized}
          getTitle={getTitle}
          onRestore={grid.restoreWindow}
          onClose={grid.closeWindow}
        />
      }
      nav={
        <NavList>
          <NavFilterLead
            open={filterOpen}
            active={filtered}
            onToggle={toggleFilter}
            onClear={() => setTopicFilter([])}
            clearDisabled={!filtered}
          />
          <div className={'crm-tools' + (filterOpen ? ' is-with-panel' : ' is-last')}>
            <input
              className="ct-input"
              type="text"
              value={q}
              placeholder="Search chats…"
              onChange={(event) => setQ(event.target.value)}
            />
          </div>
          {filterOpen ? (
            <div className="nav-topic-filter">
              <span className="nav-filter-label">Topics</span>
              <MultiSelect
                value={topicFilter}
                onChange={setTopicFilter}
                options={topicOptions}
                getOptionValue={(topic) => topic._id}
                getOptionLabel={topicLabelFor(topicOptions)}
                placeholder={topicOptions.length ? 'Any topic' : 'No topics yet'}
                clearQueryOnSelect={false}
              />
            </div>
          ) : null}
          <ChatList
            rooms={rooms}
            status={status}
            selectedId={activeId}
            userId={user?._id}
            onSelect={openChat}
            empty={q.trim() || filtered ? 'No chats match.' : 'No chats yet. Create one.'}
          />
        </NavList>
      }
      display={
        <div className="sr-grid-wrap">
          {notice && <div className="sr-grid-notice">{notice}</div>}
          <div className="sr-grid-body">
            <WindowGrid
              grid={grid}
              renderWindow={renderWindow}
              getTitle={getTitle}
              renderMenuItems={renderMenuItems}
              empty="Select a chat from the list."
            />
          </div>
        </div>
      }
    />
  )
}

export default ChatPage
